import {
  DEFAULT_PRIMARY_CTA,
  SECONDARY_CTA_VIEW_PRICING,
} from './shared-ctas';
import type { PageCopy } from './page-copy-types';

// ─── /contact ─────────────────────────────────────────────────────────────
export const CONTACT_COPY: PageCopy = {
  eyebrow: 'Get in touch',
  h1: 'Contact BizRnR',
  lede:
    'Every BizRnR customer reaches a real human within one business day. Pick the channel that fits — sales for evaluations, success for live tenants, security for vulnerability disclosure.',
  // canonical-divergence: contact page primary action starts signup; secondary action is the support inbox.
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: { label: 'Email support', href: 'mailto:support@bizrnr.com' },
  sections: [
    {
      id: 'channels',
      eyebrow: 'Channels',
      heading: 'Reach the team that owns your question',
      body:
        'Different inboxes for different intents. We answer every channel within one business day; security@ inside four business hours.',
    },
    {
      id: 'sales',
      heading: 'Sales — talking to a buyer about AI Receptionist or Enterprise',
      body:
        'Want a guided walkthrough, multi-tenant pricing, or a custom enterprise plan? Email sales@bizrnr.com or book a demo and a real human (not a chat bot) replies within one business day.',
    },
    {
      id: 'support',
      heading: 'Support — already a customer with a question',
      body:
        'Open a ticket from your dashboard or email support@bizrnr.com. AI Receptionist tenants reach us by email and in-app chat; Enterprise tenants also reach us via dedicated Slack and SMS escalation paths.',
    },
    {
      id: 'security',
      heading: 'Security — responsible disclosure',
      body:
        'Found a vulnerability? Email security@bizrnr.com with reproduction steps. We acknowledge inside four business hours, scope the impact within 24 hours, and credit researchers in our security disclosures unless you ask us not to.',
    },
    {
      id: 'press',
      heading: 'Press — interviews, podcasts, analyst briefings',
      body:
        'Press inquiries go to press@bizrnr.com. Logos, founder bio, approved quotes, and stats are all on the press kit.',
    },
  ],
  faqs: [
    {
      q: 'Do you have a phone number for support?',
      a: 'Enterprise tenants get a direct support line. AI Receptionist tenants reach us by email and in-app chat — typically faster than a phone tree, and every conversation is on the record.',
    },
    {
      q: 'How fast do you respond?',
      a: 'Sales: within one business day. Support: within one business day for AI Receptionist, within one business hour for Enterprise. Security: within four business hours, regardless of plan.',
    },
    {
      q: 'Can I talk to the founder?',
      a: 'Yes — Kristian Peter answers customer escalations directly when needed. Email founder@bizrnr.com with the context and we will route it.',
    },
  ],
  metadata: {
    title: 'Contact BizRnR — Sales, Support, Security, Press',
    description:
      'Reach the BizRnR team. Sales replies in one business day, support in one business day (faster on paid tiers), security in four business hours, press in one business day.',
    canonical: 'https://bizrnr.com/contact',
  },
};

// ─── /security ────────────────────────────────────────────────────────────
export const SECURITY_COPY: PageCopy = {
  eyebrow: 'Security & Trust',
  h1: 'Security at BizRnR',
  lede:
    'BizRnR runs the same security posture for the solo HVAC owner on AI Receptionist as it does for the call-center-replacement Enterprise customer: row-level isolation, encrypted-at-rest data, scoped vendor access, and a working responsible-disclosure path.',
  ctaPrimary: { label: 'Email security@bizrnr.com', href: 'mailto:security@bizrnr.com' },
  ctaSecondary: { label: 'Read our terms', href: '/terms' },
  sections: [
    {
      id: 'posture',
      eyebrow: 'Posture',
      heading: 'How customer data is protected',
      body:
        'Application data lives in a Postgres database where every table that holds tenant data carries a row-level-security policy. The application authenticates as the end user; the only paths that bypass RLS are explicit admin-client calls, and a custom ESLint rule fails CI when an admin-client SELECT lacks a tenant filter.',
    },
    {
      id: 'data-handling',
      heading: 'Data handling',
      bullets: [
        'In transit: TLS 1.2+ end-to-end, HSTS preload, modern cipher suites only.',
        'At rest: Postgres encrypted at the storage layer; bucket-stored assets encrypted at rest.',
        'Secrets: managed by Vercel + the platform vault. No secrets in source. Every process.env read trimmed (a custom ESLint rule enforces this).',
        'Audit log: tenant-scoped audit events for billing, auth, and admin actions; retained per tenant policy.',
      ],
    },
    {
      id: 'compliance',
      heading: 'Compliance posture',
      body:
        'Card data never touches BizRnR systems — Stripe is the PCI scope owner. Voice recordings and transcripts are stored encrypted at rest with tenant-controlled retention windows. SOC 2 Type II is in flight (target completion 2026 H2); the trust report and supporting artifacts ship to enterprise prospects under NDA in the meantime.',
    },
    {
      id: 'subprocessors',
      heading: 'Sub-processors',
      body:
        'BizRnR uses a small set of vendors to deliver the platform. Every sub-processor below has a current DPA on file, processes only the data its purpose requires, and is reviewed annually. Add or change here and the page updates.',
    },
    {
      id: 'disclosure',
      heading: 'Responsible disclosure',
      body:
        'Found a vulnerability? Email security@bizrnr.com with reproduction steps. We acknowledge within four business hours, scope the impact within 24 hours, and coordinate a fix window with you. We credit researchers in our security disclosures unless you ask us not to. We do not run a paid bounty today; legitimate findings receive recognition + swag, and material findings are escalated to founder review for case-by-case compensation.',
    },
  ],
  faqs: [
    {
      q: 'Are call recordings stored?',
      a: 'Inbound call recordings and transcripts are stored encrypted at rest. Retention defaults to 90 days; Enterprise tenants control the window from their dashboard. Recordings are never used to train models without explicit opt-in.',
    },
    {
      q: 'Where is data stored geographically?',
      a: 'Primary application data is stored in US (us-east-1). Edge static assets serve from Vercel + Cloudflare global edge. Voice runtime calls route through US Twilio + US ElevenLabs endpoints by default.',
    },
    {
      q: 'Do you support SSO?',
      a: 'Email + password and Google OAuth ship today. SAML SSO is on the Enterprise roadmap (target 2026 H2) and available earlier under contract.',
    },
    {
      q: 'How do I delete my data?',
      a: 'Cancel your subscription from billing settings; we honor a 30-day grace period (you can self-serve restore inside that window) and then purge tenant-owned data from primary storage. Backups expire on a 35-day rolling window.',
    },
  ],
  metadata: {
    title: 'Security & Trust at BizRnR — RLS, Encryption, Sub-processors',
    description:
      'How BizRnR protects customer data — Postgres RLS, encrypted-at-rest storage, scoped sub-processors, responsible disclosure, and the SOC 2 Type II roadmap.',
    canonical: 'https://bizrnr.com/security',
  },
};

// ─── /customers ───────────────────────────────────────────────────────────
export const CUSTOMERS_COPY: PageCopy = {
  eyebrow: 'Customers',
  h1: 'Built for small-business operators',
  lede:
    'Every BizRnR customer is a small-business owner who answers their own phone — until they do not have to. Below are a few of the operators running BizRnR today, with the receipts on what changed after they switched on AI reception.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: SECONDARY_CTA_VIEW_PRICING,
  sections: [
    {
      id: 'wall',
      eyebrow: 'Operators',
      heading: 'Currently answering for',
      body:
        'A live snapshot of the customers who have opted into the public directory. Names link through to each operator’s BizRnR mini-site.',
    },
    {
      id: 'outcomes',
      eyebrow: 'Workflow',
      heading: 'What BizRnR is configured to handle',
      bullets: [
        'Answer inbound calls according to the business-approved script and escalation rules.',
        'Collect approved intake details and request appointments where supported.',
        'Store call records so the business can review what happened and follow up.',
      ],
    },
    {
      id: 'verticals',
      eyebrow: 'Where BizRnR fits',
      heading: 'Verticals running today',
      body:
        'HVAC, dental, plumbing, real estate, legal, insurance, solar, restoration, medspa, auto repair, roofing, landscaping, electrical, pest control, locksmith, chiropractic, veterinary, accounting, general contractor, cleaning. Pre-tuned voice scripts ship for each.',
    },
    {
      id: 'evidence',
      eyebrow: 'Review the evidence',
      heading: 'Methods and reproducible data',
      body:
        'The research hub publishes governed methods, limitations, and downloadable data for claims that can be independently inspected. Customer outcomes are not generalized without consented, claim-level evidence.',
    },
  ],
  metadata: {
    title: 'BizRnR Customers — Operators Running AI Reception',
    description:
      'A public BizRnR customer directory plus governed product evidence, methods, and limitations. Customer outcomes are published only with consent and claim-level support.',
    canonical: 'https://bizrnr.com/customers',
  },
};

// ─── /careers ─────────────────────────────────────────────────────────────
export const CAREERS_COPY: PageCopy = {
  eyebrow: 'Careers',
  h1: 'Build BizRnR with us',
  lede:
    'BizRnR is a small team building the AI receptionist for the 33 million small businesses in the US. We hire for ownership, taste, and shipping — not for resumes. Today we are not actively hiring, but the talent pool is open.',
  ctaPrimary: { label: 'Join the talent pool', href: '#talent-pool' },
  ctaSecondary: { label: 'Read our story', href: '/press' },
  sections: [
    {
      id: 'mission',
      eyebrow: 'Mission',
      heading: 'Why we exist',
      body:
        'Small businesses miss 50–70% of inbound calls. Most callers never leave a message and never call back. That is the largest revenue leak in small-business operations and nobody else is fixing it at the price small business can afford. BizRnR closes that leak with a 24/7 AI receptionist that answers in your business voice, qualifies leads, and books appointments.',
    },
    {
      id: 'how-we-work',
      eyebrow: 'How we work',
      heading: 'The shape of the team',
      bullets: [
        'Remote-first across US time zones; quarterly in-person off-sites.',
        'Founder-led product. Engineers ship to production every day.',
        'No managers of managers. Strong individual contributors run the work.',
      ],
    },
    {
      id: 'open-roles',
      eyebrow: 'Open roles',
      heading: 'What we are hiring for right now',
      body:
        'When a role opens it shows up here automatically. Today the count is zero — we are heads-down on the autonomous support agent and the next-tier voice runtime. If a future role at BizRnR feels right, drop your details in the talent pool and we will reach out when something opens.',
    },
    {
      id: 'talent-pool',
      eyebrow: 'Talent pool',
      heading: 'Stay on our radar',
      body:
        'Email careers@bizrnr.com with a short note on what you build, links to recent work, and the role you would want at BizRnR if it existed. We read every email and reply when a relevant opening lands.',
    },
  ],
  faqs: [
    {
      q: 'Is BizRnR remote?',
      a: 'Yes. Remote-first across US time zones. We meet in person quarterly. Some founder-adjacent roles benefit from being San Diego-based; we will say so on the role description when it matters.',
    },
    {
      q: 'Do you sponsor visas?',
      a: 'Not at this team size. As we grow we expect to.',
    },
    {
      q: 'What are the comp bands?',
      a: 'Salary at the upper end of YC-late-seed ranges plus meaningful equity. Each posted role lists its band; the talent-pool email asks where you sit.',
    },
  ],
  metadata: {
    title: 'Careers at BizRnR — Build the AI Receptionist for Small Business',
    description:
      'BizRnR is a small remote-first team building the AI voice agent for 33M US small businesses. Open roles, how we work, and the talent pool for when the right role opens.',
    canonical: 'https://bizrnr.com/careers',
  },
};
